import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';

import { TableToolbar } from './TableToolbar.js';
import {
    useDataGridStyles,
    formatTime,
    MAX_ROWS,
    dateComparator,
} from './datagridutil';

function getCustomerStatus(customer) {
    if (!customer) return 'free';

    const expiresAt = new Date(customer.activeUntil.replace(' ', 'T'));
    const now = new Date();

    if (expiresAt > now) {
        if (customer.trial) return 'trial';
        return 'active';
    }

    return 'expired';
}

function UserTable(props) {
    const { users, onSelectUser, single, onEditUser } = props;
    const classes = useDataGridStyles();

    if (!(users?.length > 0)) {
        return null;
    }

    const renderStatus = ({ value }) => {
        if (value === 'free') return null;

        if (value === 'active')
            return (
                <Tooltip title={'Active'}>
                    <Icon style={{ color: 'green' }}>check_circle</Icon>
                </Tooltip>
            );

        if (value === 'expired')
            return (
                <Tooltip title={'Expired'}>
                    <Icon style={{ color: 'gray' }}>check_circle</Icon>
                </Tooltip>
            );

        if (value === 'trial')
            return (
                <Tooltip title={'Trial'}>
                    <Icon style={{ color: '#ed6c02' }}>check_circle</Icon>
                </Tooltip>
            );
    };

    const renderEdit = (params) => {
        return (
            <Tooltip title={''}>
                <span>
                    <IconButton
                        key="edit"
                        aria-label="Edit"
                        color="inherit"
                        onClick={(event) => {
                            onEditUser && onEditUser(params.value);
                            event.stopPropagation();
                        }}
                        size="large"
                    >
                        <Icon>edit</Icon>
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    const autoHeight = single || users.length <= MAX_ROWS;

    const columns = [
        {
            field: 'status',
            headerName: 'Status',
            width: 58,
            renderCell: renderStatus,
            valueGetter: (params) => {
                const now = new Date();

                const activeMembership = params.row.memberships?.find(
                    (membership) => {
                        if (membership?.Team?.Customer) {
                            const expiresAt = new Date(
                                membership.Team.Customer.activeUntil.replace(
                                    ' ',
                                    'T',
                                ),
                            );
                            return expiresAt > now;
                        }
                        return false;
                    },
                );
                const customer =
                    activeMembership?.Team?.Customer ||
                    params.row.memberships?.[0]?.Team?.Customer;

                return getCustomerStatus(customer);
            },
        },
        { field: 'id', headerName: 'ID', flex: 1 },
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1 },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 100,
            valueGetter: formatTime,
            sortComparator: dateComparator,
        },
        {
            field: 'updatedAt',
            headerName: 'Updated',
            width: 100,
            valueGetter: formatTime,
            sortComparator: dateComparator,
        },
        {
            field: 'accessedAt',
            headerName: 'Accessed',
            width: 100,
            valueGetter: formatTime,
            sortComparator: dateComparator,
        },
        {
            field: 'edit',
            headerName: '',
            width: 58,
            renderCell: renderEdit,
            valueGetter: (params) => params.row.id,
        },
    ];

    if (single) {
        columns.shift();
    } else {
        columns.pop();
    }

    return (
        <Paper
            className={classes.root}
            sx={{ height: autoHeight ? undefined : 129 + MAX_ROWS * 36 }}
        >
            <DataGrid
                slots={{ toolbar: TableToolbar }}
                slotProps={{ toolbar: { csvFileName: 'cc-users' } }}
                autoHeight={autoHeight}
                rows={users}
                columns={columns}
                hideFooter={autoHeight}
                hideFooterSelectedRowCount
                isRowSelectable={() => false}
                onRowClick={(params) =>
                    onSelectUser && onSelectUser(params.row.id)
                }
                className={
                    autoHeight ? classes.dataGridAutoHeight : classes.dataGrid
                }
                density="compact"
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                            accessedAt: !!single,
                        },
                    },
                }}
                sx={
                    single
                        ? {}
                        : {
                              '& .MuiDataGrid-row:hover': {
                                  cursor: 'pointer',
                              },
                          }
                }
            />
        </Paper>
    );
}

export default UserTable;
